import { DG } from '@cotera/sdk/core';

/**
 * Gets the editor state for an LLM column from the graph.
 * Handles both v1 and v2 column configs.
 */
export const getEditorStateFromGraph = (
  graph: DG.BuildExplorer,
  datasetName: string,
  columnName: string
): DG.DocumentEditor.DocumentState | null => {
  const column = graph.getColumn({
    rel: datasetName,
    name: columnName,
  });

  if (!column) {
    return null;
  }

  const config = column.config;
  if (config.t !== 'llm') {
    return null;
  }

  if (config.version === 'v2') {
    return config.editorState;
  }

  // Handle v1 config
  const v1Config = config as DG.Graph.LlmColConfig;
  const editorState = DG.DocumentEditor.EditorState.fromV1({
    columnName,
    llmConfig: v1Config,
  });

  return editorState.getState();
};

/**
 * Gets the LLM config values (model, schedule, temperature, maxOutputTokens) from the graph.
 */
export const getLlmConfigFromGraph = (
  graph: DG.BuildExplorer,
  datasetName: string,
  columnName: string
): {
  model: string;
  schedule: 'never' | 'daily' | 'hourly' | { cron: string };
  temperature: number;
  maxOutputTokens: number;
} | null => {
  const column = graph.getColumn({
    rel: datasetName,
    name: columnName,
  });

  if (!column) {
    return null;
  }

  const config = column.config;
  if (config.t !== 'llm') {
    return null;
  }

  if (config.version === 'v2') {
    return {
      model: config.model,
      schedule: config.schedule ?? 'never',
      temperature: config.temperature ?? 0.5,
      maxOutputTokens: config.maxOutputTokens ?? 4096,
    };
  }

  // Handle v1 config
  const v1Config = config as DG.Graph.LlmColConfig;
  return {
    model: v1Config.model,
    schedule: v1Config.when,
    temperature: v1Config.temperature ?? 0.5,
    maxOutputTokens: v1Config.maxOutputTokens ?? 4096,
  };
};

/**
 * Checks if a column is an LLM column in the graph.
 */
export const isLlmColumn = (
  graph: DG.BuildExplorer,
  datasetName: string,
  columnName: string
): boolean => {
  const column = graph.getColumn({
    rel: datasetName,
    name: columnName,
  });

  return column?.config?.t === 'llm';
};
